import React from 'react';
import {Flex, Box} from '@chakra-ui/react';
import {BooleanFilterControl} from './BooleanFilterControl';
import {NumberFilterControl} from './NumberFilterControl';
import {CategoriesFilterControl} from './CategoriesFilterControl';
import {AudienceFilterControl} from './AudienceFilterControl';
import {ScoringSignalsFilterControl} from './ScoringSignalsFilterControl';
import {IntentSignalsFilterControl} from './IntentSignalsFilterControl';
import {TagsFilterControl} from './TagsFilterControl';
import {SimilarAccountsFilterControl} from './SimilarAccountsFilterControl';
import {IntentTierFilterControl} from './IntentTierFilterControl';
import {FitTierFilterControl} from './FitTierFilterControl';
import {FilterSelect} from './FilterSelect';
import {FilterGroupControl} from './FilterGroupControl';
import {OperatorElement} from './OperatorElement';
import {AccountFilterCondition} from '../../../shared/filter/accountFilter';
import {FilterItemWithId, isAccountFilterGroupDraft} from '../filterTypes';
import {ScoringSignal} from '../../../shared/signals';
import {
  BusinessAudience,
  PrimaryBusinessCategory,
} from '../../../shared/account';
import {ObjectId} from 'bson';
import {AccountTier, IntentTier} from '../../../shared/scoredAccounts';
import {assertNever} from '../../../shared/util';
import {StateFilterControl} from './StateFilterControl';
import {SubdivisionCode} from '../../../shared/subdivisions';
import {CountryFilterControl} from './CountryFilterControl';
import {CountryCode} from '../../../shared/countries';
import {AccountFieldOrCustomField} from '../../../shared/signalDefinition';

const FilterFieldLabels: Partial<
  Record<AccountFilterCondition['field'], string>
> = {
  tier: 'Fit Tier',
  intentTier: 'Intent Tier',
  signals: 'Fit Signals',
  matchedIntentFields: 'Intent Signals',
  similarTo: 'Similar Accounts',
  primaryCategory: 'Categories',
  tags: 'Lists',
  audience: 'Audiences',
  inCRM: 'Included in CRM',
  inSAM: 'Included in SAM',
  employees: 'Employees',
  funding: 'Funding',
  trafficRank: 'Traffic Rank',
  hqCountry: 'HQ Country',
  hqState: 'HQ State/Province',
};

// Helper to create a default condition for a given filter field
const createDefaultCondition = (
  field: AccountFilterCondition['field']
): AccountFilterCondition => {
  switch (field) {
    case 'tier':
      return {operator: 'in', field: 'tier', value: ['A', 'B', 'C', 'D']};
    case 'signals':
      return {operator: 'in', field: 'signals', value: []};
    case 'primaryCategory':
      return {operator: 'in', field: 'primaryCategory', value: []};
    case 'audience':
      return {operator: 'in', field: 'audience', value: []};
    case 'tags':
      return {operator: 'in', field: 'tags', value: []};
    case 'inCRM':
      return {operator: 'equals', field: 'inCRM', value: true};
    case 'inSAM':
      return {operator: 'equals', field: 'inSAM', value: true};
    case 'employees':
      return {operator: 'range', field: 'employees', value: {}};
    case 'funding':
      return {operator: 'range', field: 'funding', value: {}};
    case 'trafficRank':
      return {operator: 'range', field: 'trafficRank', value: {}};
    case 'similarTo':
      return {operator: 'in', field: 'similarTo', value: []};
    case 'intentTier':
      return {
        operator: 'in',
        field: 'intentTier',
        value: [],
      };
    case 'hqState':
      return {operator: 'in', field: 'hqState', value: []};
    case 'hqCountry':
      return {operator: 'in', field: 'hqCountry', value: []};
    case 'matchedIntentFields':
      return {operator: 'in', field: 'matchedIntentFields', value: []};

    case 'intent':
    case 'active':
    case 'disqualified':
    case 'excluded':
    case 'saved':
    case 'imported':
      throw new Error('not implemented');

    default:
      assertNever(field);
  }
};

// Component to render filter content (field selector + value control + remove button)
// This is used both for top-level filters and filters within groups
export const FilterConditionContent = ({
  condition,
  availableOptions,
  onUpdate,
  onRemove,
  staticTagsOnly,
}: {
  condition: AccountFilterCondition | undefined;
  availableOptions: AccountFilterCondition['field'][];
  onUpdate: (condition: AccountFilterCondition) => void;
  onRemove: () => void;
  staticTagsOnly?: boolean;
}) => {
  const filterTypeSelector = (
    <Box width="170px" flexShrink={0}>
      <FilterSelect
        placeholder="Select filter..."
        options={availableOptions.map((field) => ({
          label: FilterFieldLabels[field] || field,
          value: field,
        }))}
        value={
          condition
            ? {
                label: FilterFieldLabels[condition.field] || condition.field,
                value: condition.field,
              }
            : undefined
        }
        onChange={(selected) => {
          if (selected) {
            const newField = selected.value as AccountFilterCondition['field'];
            const newCondition = createDefaultCondition(newField);
            onUpdate(newCondition);
          }
        }}
      />
    </Box>
  );

  // If no condition type selected yet, show disabled placeholder
  if (!condition) {
    return (
      <>
        {filterTypeSelector}
        <BooleanFilterControl
          isDisabled
          value={undefined}
          onChange={() => {}}
          onRemove={onRemove}
        />
      </>
    );
  }

  return (
    <>
      {filterTypeSelector}

      {condition.operator === 'in' && condition.field === 'signals' && (
        <ScoringSignalsFilterControl
          value={condition.value as ScoringSignal[]}
          negate={condition.negate}
          onChange={(value, negate) => onUpdate({...condition, value, negate})}
          onRemove={onRemove}
        />
      )}

      {condition.operator === 'in' &&
        condition.field === 'matchedIntentFields' && (
          <IntentSignalsFilterControl
            value={condition.value as AccountFieldOrCustomField[]}
            negate={condition.negate}
            onChange={(value, negate) =>
              onUpdate({
                ...condition,
                value,
                negate,
              })
            }
            onRemove={onRemove}
          />
        )}

      {condition.operator === 'in' && condition.field === 'audience' && (
        <AudienceFilterControl
          value={condition.value as BusinessAudience[]}
          negate={condition.negate}
          onChange={(value, negate) => onUpdate({...condition, value, negate})}
          onRemove={onRemove}
        />
      )}

      {condition.operator === 'in' && condition.field === 'tags' && (
        <TagsFilterControl
          value={condition.value as ObjectId[]}
          negate={condition.negate}
          onChange={(value, negate) => onUpdate({...condition, value, negate})}
          onRemove={onRemove}
          staticTagsOnly={staticTagsOnly}
        />
      )}

      {condition.operator === 'in' && condition.field === 'primaryCategory' && (
        <CategoriesFilterControl
          value={condition.value as PrimaryBusinessCategory[]}
          negate={condition.negate}
          onChange={(value, negate) => onUpdate({...condition, value, negate})}
          onRemove={onRemove}
        />
      )}

      {condition.operator === 'equals' && condition.field === 'inCRM' && (
        <BooleanFilterControl
          value={condition.value}
          onChange={(value) => onUpdate({...condition, value})}
          onRemove={onRemove}
        />
      )}

      {condition.operator === 'equals' && condition.field === 'inSAM' && (
        <BooleanFilterControl
          value={condition.value}
          onChange={(value) => onUpdate({...condition, value})}
          onRemove={onRemove}
        />
      )}

      {condition.operator === 'range' && condition.field === 'employees' && (
        <NumberFilterControl
          value={condition.value}
          onChange={(value) => onUpdate({...condition, value})}
          onRemove={onRemove}
        />
      )}

      {condition.operator === 'range' && condition.field === 'funding' && (
        <NumberFilterControl
          value={condition.value}
          onChange={(value) => onUpdate({...condition, value})}
          onRemove={onRemove}
          format="currency"
        />
      )}

      {condition.operator === 'range' && condition.field === 'trafficRank' && (
        <NumberFilterControl
          value={condition.value}
          onChange={(value) => onUpdate({...condition, value})}
          onRemove={onRemove}
        />
      )}

      {condition.operator === 'in' && condition.field === 'similarTo' && (
        <SimilarAccountsFilterControl
          value={condition.value as ObjectId[]}
          negate={condition.negate}
          onChange={(value, negate) => onUpdate({...condition, value, negate})}
          onRemove={onRemove}
        />
      )}

      {condition.operator === 'in' && condition.field === 'intentTier' && (
        <IntentTierFilterControl
          value={condition.value as IntentTier[]}
          negate={condition.negate}
          onChange={(value, negate) => onUpdate({...condition, value, negate})}
          onRemove={onRemove}
        />
      )}

      {condition.operator === 'in' && condition.field === 'tier' && (
        <FitTierFilterControl
          value={condition.value as AccountTier[]}
          negate={condition.negate}
          onChange={(value, negate) => onUpdate({...condition, value, negate})}
          onRemove={onRemove}
        />
      )}

      {condition.operator === 'in' && condition.field === 'hqCountry' && (
        <CountryFilterControl
          value={condition.value as CountryCode[]}
          negate={condition.negate}
          onChange={(value, negate) => onUpdate({...condition, value, negate})}
          onRemove={onRemove}
        />
      )}

      {condition.operator === 'in' && condition.field === 'hqState' && (
        <StateFilterControl
          value={condition.value as SubdivisionCode[]}
          negate={condition.negate}
          onChange={(value, negate) => onUpdate({...condition, value, negate})}
          onRemove={onRemove}
        />
      )}
    </>
  );
};

export const FilterControl = ({
  index,
  filterItem,
  availableOptions,
  filterOperator,
  onOperatorChange,
  onUpdateCondition,
  onRemove,
  staticTagsOnly,
}: {
  index: number;
  filterItem: FilterItemWithId;
  availableOptions: AccountFilterCondition['field'][];
  filterOperator: 'and' | 'or';
  onOperatorChange: (operator: 'and' | 'or') => void;
  onUpdateCondition: (condition: AccountFilterCondition) => void;
  onRemove: () => void;
  staticTagsOnly?: boolean;
}) => {
  return (
    <Flex gap={2} alignItems="flex-start">
      <Box width="85px" flexShrink={0}>
        <OperatorElement
          operator={filterOperator}
          isEditable={index === 0}
          onOperatorChange={onOperatorChange}
        />
      </Box>
      {isAccountFilterGroupDraft(filterItem) ? (
        <FilterGroupControl
          group={filterItem}
          availableOptions={availableOptions}
          onRemove={onRemove}
          staticTagsOnly={staticTagsOnly}
        />
      ) : (
        <FilterConditionContent
          condition={filterItem.item}
          availableOptions={availableOptions}
          onUpdate={onUpdateCondition}
          onRemove={onRemove}
          staticTagsOnly={staticTagsOnly}
        />
      )}
    </Flex>
  );
};
